import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Briefcase, Filter, Settings, Home, Clock, ChevronRight, X, Clipboard, MapPin, Shield, Zap, BarChart3, DollarSign, Calculator, ChevronLeft, UserCheck, Plane, MessageSquare, CreditCard, Gift, Power, FileText } from 'lucide-react';
import '../Sidebar.css';
import { useAdvancedPermissions } from '../hooks/useAdvancedPermissions';
import { useMessaging } from '../contexts/MessagingContext';
import { useSubscriptionAccess } from '../hooks/useSubscriptionAccess';
import { isSubscriptionModuleEnabled } from '../config/subscriptionConfig';

const SidebarItem = ({ icon: Icon, label, onClick, isActive, hasSubmenu, children, isOpen, toggleSubmenu, isCollapsed, showLabelInContextMenu, forceShowLabel, badge }) => {
  const [showContextMenu, setShowContextMenu] = useState(false);
  const itemRef = useRef(null);
  const hoverTimeoutRef = useRef(null);

  const handleClick = () => {
    if (hasSubmenu && isCollapsed) {
      setShowContextMenu(!showContextMenu);
    } else if (hasSubmenu && label !== 'Bénéficiaires' && !isCollapsed) {
      toggleSubmenu();
    } else {
      onClick();
    }
  };

  const handleMouseEnter = () => {
    if (isCollapsed && hasSubmenu) {
      setShowContextMenu(true);
    }
  };

  const handleMouseLeave = () => {
    if (isCollapsed && hasSubmenu) {
      // Ajouter un délai pour permettre la navigation vers les sous-menus
      hoverTimeoutRef.current = setTimeout(() => {
        setShowContextMenu(false);
      }, 300); // Délai de 300ms
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (itemRef.current && !itemRef.current.contains(event.target)) {
        setShowContextMenu(false);
      }
    };

    if (showContextMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      
      // Calculer et appliquer la position verticale après le rendu
      setTimeout(() => {
        if (itemRef.current) {
          const rect = itemRef.current.getBoundingClientRect();
          const menuElement = itemRef.current.querySelector('.sb-context-menu');
          if (menuElement) {
            // Soustraire la hauteur de l'en-tête pour aligner correctement
            const headerHeight = 80; // Hauteur approximative de l'en-tête
            menuElement.style.top = `${rect.top - headerHeight}px`;
            menuElement.classList.add('positioned');
          }
        }
      }, 10);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, [showContextMenu]);

  return (
    <div ref={itemRef} className="sb-item-wrapper">
      <div
        className={`sb-item ${isActive ? 'active' : ''} ${isCollapsed ? 'collapsed' : ''}`}
        onClick={handleClick}
        onMouseEnter={isCollapsed && hasSubmenu ? handleMouseEnter : undefined}
        onMouseLeave={isCollapsed && hasSubmenu ? handleMouseLeave : undefined}
        title={isCollapsed && hasSubmenu ? `${label} (Survolez ou cliquez pour le menu)` : isCollapsed ? label : ''}
      >
        <div className="sb-item-content">
          <Icon size={20} className="sb-item-icon" />
          {(!isCollapsed || showLabelInContextMenu) && <span className="sb-item-label">{label}</span>}
          {badge !== undefined && badge > 0 && (
            <span className="sb-item-badge">{badge > 99 ? '99+' : badge}</span>
          )}
        </div>
        {hasSubmenu && !isCollapsed && (
          <ChevronRight
            size={16}
            className={`sb-submenu-icon ${isOpen ? 'open' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              toggleSubmenu();
            }}
          />
        )}
      </div>
      
      {/* Menu contextuel pour le mode réduit */}
      {isCollapsed && hasSubmenu && showContextMenu && (
        <div 
          className="sb-context-menu"
          onMouseEnter={() => {
            // Annuler le délai de fermeture si la souris entre dans le menu
            if (hoverTimeoutRef.current) {
              clearTimeout(hoverTimeoutRef.current);
            }
          }}
          onMouseLeave={() => {
            // Fermer le menu après un délai quand la souris quitte
            hoverTimeoutRef.current = setTimeout(() => {
              setShowContextMenu(false);
            }, 300);
          }}
        >
          <div className="sb-context-menu-header">
            <Icon size={16} />
            <span>{label}</span>
          </div>
          <div className="sb-context-menu-items">
            {children && React.Children.map(children, child => {
              if (React.isValidElement(child)) {
                return React.cloneElement(child, { 
                  isCollapsed: false,
                  showLabelInContextMenu: true,
                  onClick: () => {
                    if (child.props.onClick) {
                      child.props.onClick();
                    }
                    setShowContextMenu(false);
                  }
                });
              }
              return null;
            })}
          </div>
        </div>
      )}

      {/* Sous-menu normal pour le mode étendu */}
      {isOpen && children && !isCollapsed && (
        <div className="sb-submenu">
          {children}
        </div>
      )}
    </div>
  );
};

const Sidebar = ({ setCurrentView, currentView, isOpen, toggleSidebar, selectedClient, setSelectedClient, currentUser, employeeAppointments, addNotification, db, setIsSidebarOpen, handleViewChange }) => {
  const [showAnalysesSubmenu, setShowAnalysesSubmenu] = useState(false);
  const [showOperationsSubmenu, setShowOperationsSubmenu] = useState(false);
  const [showAdminSubmenu, setShowAdminSubmenu] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const sidebarRef = useRef(null);

  // Utiliser le nouveau système de permissions
  const { hasPermission } = useAdvancedPermissions({ userId: currentUser?.id });

  // Vérifier si le module d'abonnement est activé
  const subscriptionModuleEnabled = isSubscriptionModuleEnabled();

  // Vérifier l'accès basé sur l'abonnement
  const { hasPremiumAccess } = useSubscriptionAccess();

  // Si le module est désactivé, donner accès premium à tous
  const hasSubscriptionAccess = !subscriptionModuleEnabled || hasPremiumAccess;

  // Récupérer les compteurs de messages non lus
  const messagingContext = useMessaging();
  const unreadCounts = messagingContext?.unreadCounts || { internal: 0, client: 0, group: 0 };
  const totalUnread = unreadCounts.internal + unreadCounts.client + unreadCounts.group;

  // Utiliser la fonction handleViewChange passée en prop si elle existe, sinon utiliser la fonction locale
  const handleViewChangeWrapper = (view) => {
    if (handleViewChange) {
      // Utiliser la fonction passée depuis MainLayout qui met à jour l'URL
      handleViewChange(view);
    } else {
      // Fallback à l'ancienne méthode
      setCurrentView(view);
    }

    // Fermer automatiquement le sidebar après avoir cliqué sur un menu sur mobile
    if (isOpen && window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
  };

  const isSettingsActive = ['generalSettings', 'rolesPositions', 'planningsettings'].includes(currentView);

  return (
    <>
      <div ref={sidebarRef} className={`sb-container ${isOpen ? 'open' : ''} ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="sb-header">
          {!isCollapsed && <h2 className="sb-title">Menu</h2>}
          <div className="sb-header-actions">
            {window.innerWidth >= 768 && (
              <button 
                onClick={() => setIsCollapsed(!isCollapsed)} 
                className="sb-collapse-button"
                title={isCollapsed ? "Développer" : "Réduire"}
              >
                <ChevronLeft size={20} className={`sb-collapse-icon ${isCollapsed ? 'collapsed' : ''}`} />
              </button>
            )}
            <button 
              onClick={() => setIsSidebarOpen(false)} 
              className="sb-close-button"
            >
              <X size={24} />
            </button>
          </div>
        </div>
        <nav className="sb-nav">
        <SidebarItem 
          icon={Home} 
          label="Tableau de bord" 
          onClick={() => handleViewChangeWrapper('dashboard')} 
          isActive={currentView === 'dashboard'}
          isCollapsed={isCollapsed}
        />
          
        

        {/* PLANNING - gestion des rendez-vous */}
        {hasSubscriptionAccess && hasPermission({ resource: 'appointments', action: 'read' }) && (
          <SidebarItem
            icon={Calendar}
            label="Planning"
            onClick={() => handleViewChangeWrapper('planning')}
            isActive={currentView === 'planning'}
            isCollapsed={isCollapsed}
          />
        )}
        {/* OUTILS - éléments divers */}
        {hasSubscriptionAccess && hasPermission({ resource: 'messaging', action: 'read' }) && (
          <SidebarItem
            icon={MessageSquare}
            label="Messagerie"
            onClick={() => handleViewChangeWrapper('messaging')}
            isActive={currentView === 'messaging'}
            isCollapsed={isCollapsed}
            badge={totalUnread}
          />
        )}

        {/* ABONNEMENT - gestion des abonnements (visible uniquement si le module est activé) */}
        {subscriptionModuleEnabled && (
          <SidebarItem
            icon={CreditCard}
            label="Abonnement"
            onClick={() => window.location.href = '/subscription'}
            isActive={false}
            isCollapsed={isCollapsed}
          />
        )}

        {/* COMPTABILITÉ - gestion financière */}
        {hasSubscriptionAccess && hasPermission({ resource: 'accounting', action: 'read' }) && (
          <SidebarItem
            icon={Calculator}
            label="Comptabilité"
            onClick={() => handleViewChangeWrapper('accounting')}
            isActive={currentView === 'accounting'}
            isCollapsed={isCollapsed}
          />
        )}

        {/* RESSOURCES - gestion complète des intervenants et bénéficiaires */}
        {hasSubscriptionAccess && hasPermission({ resource: 'resources', action: 'read' }) && (
          <SidebarItem
            icon={UserCheck}
            label="Ressources"
            onClick={() => handleViewChangeWrapper('resources')}
            isActive={currentView === 'resources'}
            isCollapsed={isCollapsed}
          />
        )}

        {/* RAPPORTS - statistiques et analyses des intervenants */}
        {hasSubscriptionAccess && hasPermission({ resource: 'reports', action: 'read' }) && (
          <SidebarItem
            icon={BarChart3}
            label="Rapports"
            onClick={() => handleViewChangeWrapper('employeeReports')}
            isActive={currentView === 'employeeReports'}
            isCollapsed={isCollapsed}
          />
        )}


        {hasSubscriptionAccess && hasPermission({ resource: 'map', action: 'read' }) && (
          <SidebarItem
            icon={MapPin}
            label="Carte"
            onClick={() => handleViewChangeWrapper('mapView')}
            isActive={currentView === 'mapView'}
            isCollapsed={isCollapsed}
          />
        )}
        {/* ADMINISTRATION SYSTÈME - regroupement configuration avancée */}
        {hasSubscriptionAccess && (hasPermission({ resource: 'settings', action: 'write' }) ||
          hasPermission({ resource: 'roles', action: 'manage' }) ||
          hasPermission({ resource: 'tasks', action: 'write' }) ||
          hasPermission({ resource: 'payments', action: 'manage' })) && (
          <SidebarItem
            icon={Settings}
            label="Administration"
            onClick={() => {
              if (hasPermission({ resource: 'settings', action: 'write' })) {
                handleViewChangeWrapper('generalSettings');
              } else if (hasPermission({ resource: 'roles', action: 'manage' })) {
                handleViewChangeWrapper('rolesManagement');
              } else if (hasPermission({ resource: 'tasks', action: 'write' })) {
                handleViewChangeWrapper('TaskManager');
              }
            }}
            isActive={isSettingsActive || currentView === 'rolesManagement' || currentView === 'TaskManager'}
            hasSubmenu
            isOpen={showAdminSubmenu}
            toggleSubmenu={() => setShowAdminSubmenu(!showAdminSubmenu)}
            isCollapsed={isCollapsed}
          >
            {hasPermission({ resource: 'settings', action: 'write' }) && (
              <SidebarItem icon={Settings} label="Paramètres" onClick={() => handleViewChangeWrapper('generalSettings')} isActive={currentView === 'generalSettings'} isCollapsed={isCollapsed} />
            )}
            {hasPermission({ resource: 'tasks', action: 'write' }) && (
              <SidebarItem icon={Clipboard} label="Tâches" onClick={() => handleViewChangeWrapper('TaskManager')} isActive={currentView === 'TaskManager'} isCollapsed={isCollapsed} />
            )}
            {hasPermission({ resource: 'roles', action: 'manage' }) && (
              <SidebarItem icon={Shield} label="Rôles" onClick={() => handleViewChangeWrapper('rolesManagement')} isActive={currentView === 'rolesManagement'} isCollapsed={isCollapsed} />
            )}
            {hasPermission({ resource: 'settings', action: 'write' }) && (
              <SidebarItem icon={Settings} label="Templates Dashboard" onClick={() => handleViewChangeWrapper('dashboardTemplates')} isActive={currentView === 'dashboardTemplates'} isCollapsed={isCollapsed} />
            )}
            {subscriptionModuleEnabled && hasPermission({ resource: 'payments', action: 'manage' }) && (
              <SidebarItem icon={CreditCard} label="Configuration Stripe" onClick={() => window.location.href = '/stripe-config'} isActive={false} isCollapsed={isCollapsed} />
            )}
            {subscriptionModuleEnabled && hasPermission({ resource: 'payments', action: 'manage' }) && (
              <SidebarItem icon={Gift} label="Gestion abonnements" onClick={() => window.location.href = '/subscription-management'} isActive={false} isCollapsed={isCollapsed} />
            )}
            {hasPermission({ resource: 'payments', action: 'manage' }) && (
              <SidebarItem icon={Power} label="Module abonnement" onClick={() => handleViewChangeWrapper('subscriptionModuleConfig')} isActive={currentView === 'subscriptionModuleConfig'} isCollapsed={isCollapsed} />
            )}
            {hasPermission({ resource: 'settings', action: 'write' }) && (
              <SidebarItem icon={FileText} label="Contrats de mandat" onClick={() => window.location.href = '/beneficiary-mandates'} isActive={false} isCollapsed={isCollapsed} />
            )}
          </SidebarItem>
        )}
        </nav>
      </div>
      {/* Overlay pour fermer le sidebar sur mobile */}
      {isOpen && window.innerWidth < 768 && (
        <div 
          className="sb-overlay"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </>
  );
};

export default Sidebar;
